import React, { useState, useEffect } from 'react';
import { Check, X, Loader2 } from 'lucide-react';
import { FollowRequest, getFollowRequests, acceptFollowRequest, rejectFollowRequest } from '../../lib/follow';

export function FollowRequests() {
  const [requests, setRequests] = useState<FollowRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const data = await getFollowRequests();
      setRequests(data);
    } catch (error) {
      console.error('Error loading follow requests:', error);
      setError('Fehler beim Laden der Anfragen');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (requestId: string) => {
    setProcessingIds(prev => new Set(prev).add(requestId));
    try {
      await acceptFollowRequest(requestId);
      setRequests(prev => prev.filter(request => request.id !== requestId));
    } catch (error) {
      console.error('Error accepting follow request:', error);
      setError('Fehler beim Akzeptieren der Anfrage');
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(requestId);
        return newSet;
      });
    }
  };

  const handleReject = async (requestId: string) => {
    setProcessingIds(prev => new Set(prev).add(requestId));
    try {
      await rejectFollowRequest(requestId);
      setRequests(prev => prev.filter(request => request.id !== requestId));
    } catch (error) {
      console.error('Error rejecting follow request:', error);
      setError('Fehler beim Ablehnen der Anfrage');
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(requestId);
        return newSet;
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500 bg-red-500/10 border border-red-500/50">
        {error}
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center p-4 text-text-secondary flex flex-col items-center gap-2">
        <p>Keine ausstehenden Freundschaftsanfragen</p>
        <p className="text-sm">Hier erscheinen Anfragen von anderen Künstlern</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <h2 className="text-lg font-bold mb-4">Freundschaftsanfragen</h2>
      {requests.map((request) => (
        <div
          key={request.id}
          className="flex items-center justify-between p-4 bg-background-secondary rounded-lg"
        >
          <div className="flex items-center gap-3">
            <img
              src={request.profiles?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${request.profiles?.username}`}
              alt={request.profiles?.username}
              className="w-10 h-10 rounded-full"
            />
            <div>
              <div className="font-medium">{request.profiles?.username}</div>
              {request.profiles?.artist_types[0] && (
                <div className="text-sm text-text-secondary">
                  {request.profiles.artist_types[0]}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => handleAccept(request.id)}
              disabled={processingIds.has(request.id)}
              className="p-2 bg-accent-primary hover:bg-accent-hover text-white rounded-full disabled:opacity-50"
            >
              {processingIds.has(request.id) ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Check size={18} />
              )}
            </button>
            <button
              onClick={() => handleReject(request.id)}
              disabled={processingIds.has(request.id)}
              className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-full disabled:opacity-50"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}